import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { BrandIcon } from './icons';
import { STUDIO } from '../data/studio';
import { getActiveKey, HOME_PATHS } from '../utils/navActive';
import { usePressSpring } from '../hooks/usePressSpring';
import { useTilt } from '../hooks/useTilt';
import { useHomeScrollSpy } from '../hooks/useHomeScrollSpy';
import { useLanguage } from '../i18n/LanguageContext';
import { cx } from '../utils/cx';
import mixins from '../styles/mixins.module.css';
import styles from './Nav.module.css';

const LINKS = [
  { k: 'home', to: '/', labelKey: 'navHome' },
  { k: 'apps', to: '/apps', labelKey: 'navApps' },
  { k: 'studio', to: '/studio', labelKey: 'navStudio' },
  { k: 'contact', to: '/contact', labelKey: 'navContact' },
];

export default function Nav() {
  const { t } = useLanguage();
  const { pathname } = useLocation();
  const spyKey = useHomeScrollSpy(HOME_PATHS.includes(pathname));
  const activeKey = spyKey || getActiveKey(pathname);
  const linkRefs = useRef({});
  const pillRef = useRef(null);
  const [hidden, setHidden] = useState(false);
  const ctaRef = usePressSpring(mixins.pressed);
  const glassRef = useTilt({ tilt: false });

  const movePill = () => {
    const pill = pillRef.current;
    const active = linkRefs.current[activeKey];
    if (!pill) return;
    if (!active) {
      pill.style.opacity = 0;
      return;
    }
    pill.style.opacity = 1;
    pill.style.width = active.offsetWidth + 'px';
    pill.style.translate = active.offsetLeft + 'px 0';
  };

  useLayoutEffect(movePill, [activeKey]);

  useEffect(() => {
    const onResize = () => movePill();
    window.addEventListener('resize', onResize);
    document.fonts && document.fonts.ready.then(movePill);
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      setHidden(y > lastY && y > 300);
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header ref={glassRef} className={cx(mixins.glass, styles.nav, hidden && styles.hide)} id="nav">
      <Link to="/" className={styles.brand} aria-label="OnyxUp home">
        <img src="/images/onyxup_logo.svg" alt="" />
        OnyxUp
      </Link>

      <nav className={styles.navLinks}>
        <span ref={pillRef} className={styles.navPill} />
        {LINKS.map((l) => (
          <Link
            key={l.k}
            ref={(el) => {
              linkRefs.current[l.k] = el;
            }}
            to={l.to}
            className={cx(styles.navLink, activeKey === l.k && styles.active)}
          >
            {t(l.labelKey)}
          </Link>
        ))}
      </nav>

      <ThemeToggle />

      <a
        ref={ctaRef}
        className={cx(styles.navCta, mixins.spring)}
        href={STUDIO.developerUrl}
        target="_blank"
        rel="noopener noreferrer"
      >
        <BrandIcon name="apple" />
        <span>{t('navAppStore')}</span>
      </a>
    </header>
  );
}
